import { z } from "zod";
import { liraConversationStateSchema } from "./lira-context.js";
import { MUNICIPAL_ADMIN_ASSIGNABLE_ROLES, PERMISSION_CODES, ROLE_CODES, } from "./access.js";
export const accountStatusSchema = z.enum(["ACTIVE", "SUSPENDED", "DEACTIVATED"]);
// Runtime account creation/assignment accepts only the seven active roles.
// Session typing remains RoleCode so an old BARANGAY_WEB_PORTAL session can be
// read safely while migration 0019 converts it to BARANGAY_ENCODER.
export const roleCodeSchema = z.enum(ROLE_CODES);
export const municipalityAdminAssignableRoleSchema = z.enum(MUNICIPAL_ADMIN_ASSIGNABLE_ROLES);
export const permissionCodeSchema = z.enum(PERMISSION_CODES);
export const geographicScopeSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("PROVINCE"), provinceId: z.uuid() }),
    z.object({ type: z.literal("MUNICIPALITY_CITY"), municipalityId: z.uuid() }),
    z.object({ type: z.literal("BARANGAY"), barangayId: z.uuid() }),
]);
export const loginRequestSchema = z.object({
    username: z.string().trim().min(3).max(100),
    password: z.string().min(1).max(512),
});
export const createUserRequestSchema = z.object({
    username: z.string().trim().min(3).max(100).regex(/^[a-zA-Z0-9._-]+$/),
    displayName: z.string().trim().min(2).max(160),
    password: z.string().min(12).max(512),
    status: accountStatusSchema.default("ACTIVE"),
    role: municipalityAdminAssignableRoleSchema,
    scope: geographicScopeSchema,
});
export const updateAccountStatusRequestSchema = z.object({ status: accountStatusSchema });
export const resetAccountAccessRequestSchema = z.object({ temporaryPassword: z.string().min(12).max(512) });
export const updateAssignmentRequestSchema = z.object({
    role: municipalityAdminAssignableRoleSchema,
    scope: geographicScopeSchema,
});
export const updatePermissionGrantsRequestSchema = z.object({
    grants: z.array(permissionCodeSchema).max(PERMISSION_CODES.length),
    denials: z.array(permissionCodeSchema).max(PERMISSION_CODES.length),
});
const householdUpdateFieldsSchema = z.object({
    householdHead: z.string().trim().min(1).max(1_000).optional(),
    respondentName: z.string().trim().min(1).max(1_000).optional(),
    address: z.string().trim().min(1).max(1_000).optional(),
    purok: z.string().trim().max(1_000).optional(),
    householdType: z.enum(["Household", "Institutional Living Quarter"]).optional(),
}).strict();
export const updateHouseholdInformationRequestSchema = householdUpdateFieldsSchema.refine((value) => Object.keys(value).length > 0, "At least one permitted household field must be provided.");
export const recordReviewDecisionSchema = z.enum(["START_REVIEW", "APPROVE", "RETURN"]);
export const recordReviewRequestSchema = z.object({
    decision: recordReviewDecisionSchema,
    remarks: z.string().trim().max(1_000).default(""),
}).strict().superRefine((value, context) => {
    if (value.decision === "RETURN" && !value.remarks) {
        context.addIssue({ code: "custom", path: ["remarks"], message: "Correction remarks are required when returning a record." });
    }
});
export const residentLifecycleStatusSchema = z.enum([
    "ACTIVE",
    "DECEASED",
    "MOVED_OUT",
    "TRANSFERRED",
    "INACTIVE",
]);
const residentUpdateFieldsSchema = z.object({
    firstName: z.string().trim().min(1).max(1_000).optional(),
    middleName: z.string().trim().max(1_000).optional(),
    lastName: z.string().trim().min(1).max(1_000).optional(),
    civilStatus: z.string().trim().max(1_000).optional(),
    disability: z.string().trim().max(1_000).optional(),
    registeredSeniorCitizen: z.string().trim().max(1_000).optional(),
    occupation: z.string().trim().max(1_000).optional(),
}).strict();
export const updateResidentInformationRequestSchema = residentUpdateFieldsSchema.refine((value) => Object.keys(value).length > 0, "At least one permitted resident field must be provided.");
export const updateResidentLifecycleRequestSchema = z.object({
    status: residentLifecycleStatusSchema,
    changeDate: z.string().date(),
    reason: z.string().trim().max(1_000).default(""),
    destination: z.string().trim().max(1_000).default(""),
}).strict().superRefine((value, context) => {
    if (value.status !== "ACTIVE" && !value.reason) {
        context.addIssue({ code: "custom", path: ["reason"], message: "A reason or remark is required for this status change." });
    }
    if ((value.status === "MOVED_OUT" || value.status === "TRANSFERRED") && !value.destination) {
        context.addIssue({ code: "custom", path: ["destination"], message: "A movement destination is required." });
    }
});
export const liraQueryRequestSchema = z.object({
    message: z.string().trim().min(1).max(1_000),
    barangayId: z.uuid().nullable().optional(),
    conversation: liraConversationStateSchema.nullable().optional(),
});
//# sourceMappingURL=contracts.js.map